import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Grid,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Timer,
  Groups,
  BarChart,
  Calculate,
  ExpandMore,
  TrendingUp,
  EmojiEvents,
  HealthAndSafety
} from '@mui/icons-material';
import PublicHeader from '../PublicHeader/PublicHeader';
import {
  Page,
  HeroSection,
  HeroGrid,
  HeroContent,
  HeroTitle,
  HeroSubtitle,
  CTAButton,
  GhostButton,
  FeatureCard,
  FeatureIcon,
  StatsBox,
  StatsNumber,
  SectionHeading,
  SectionSubheading,
  StepCard,
  StepNumber,
  BenefitCard,
  CTASection,
  CTAInner,
  Glow
} from './LandingPage.styles';

function LandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const features = [
    {
      icon: <Timer fontSize="large" />,
      title: t('featureTimerTitle'),
      description: t('featureTimerDesc')
    },
    {
      icon: <Groups fontSize="large" />,
      title: t('featureTeamsTitle'),
      description: t('featureTeamsDesc')
    },
    {
      icon: <BarChart fontSize="large" />,
      title: t('featureStatsTitle'),
      description: t('featureStatsDesc')
    },
    {
      icon: <Calculate fontSize="large" />,
      title: t('featureCalculatorTitle'),
      description: t('featureCalculatorDesc')
    }
  ];

  const benefits = [
    {
      icon: <HealthAndSafety fontSize="large" color="primary" />,
      title: t('benefitHealthTitle'),
      description: t('benefitHealthDesc')
    },
    {
      icon: <TrendingUp fontSize="large" color="primary" />,
      title: t('benefitProductivityTitle'),
      description: t('benefitProductivityDesc')
    },
    {
      icon: <EmojiEvents fontSize="large" color="primary" />,
      title: t('benefitMotivationTitle'),
      description: t('benefitMotivationDesc')
    }
  ];

  const faqs = [
    {
      question: t('faqQuestion1'),
      answer: t('faqAnswer1')
    },
    {
      question: t('faqQuestion2'),
      answer: t('faqAnswer2')
    },
    {
      question: t('faqQuestion3'),
      answer: t('faqAnswer3')
    },
    {
      question: t('faqQuestion4'),
      answer: t('faqAnswer4')
    },
    {
      question: t('faqQuestion5'),
      answer: t('faqAnswer5')
    }
  ];

  return (
    <Page>
      <PublicHeader />

      <HeroSection>
        <Glow top="-160px" left="-180px" size="540px" />
        <Glow top="320px" left="68%" size="460px" />
        <Container maxWidth="lg">
          <HeroGrid>
            <HeroContent>
              <HeroTitle variant="h1">{t('heroTitle')}</HeroTitle>
              <HeroSubtitle variant="h6">{t('heroSubtitle')}</HeroSubtitle>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <CTAButton variant="contained" size="large" onClick={() => navigate('/login')}>
                  {t('getStartedFree')}
                </CTAButton>
                <GhostButton variant="outlined" size="large" onClick={() => navigate('/blog')}>
                  {t('learnMore')}
                </GhostButton>
              </Stack>
            </HeroContent>

          </HeroGrid>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <SectionHeading variant="h3">{t('teamStatistics')}</SectionHeading>
        <SectionSubheading variant="body1">{t('featureStatsDesc')}</SectionSubheading>
        <Box
          sx={{
            display: 'grid',
            gap: 4,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
          }}
        >
          <StatsBox>
            <StatsNumber variant="h3">5,000+</StatsNumber>
            <Typography variant="body1" color="text.secondary">
              {t('activeUsers')}
            </Typography>
          </StatsBox>
          <StatsBox>
            <StatsNumber variant="h3">50,000+</StatsNumber>
            <Typography variant="body1" color="text.secondary">
              {t('standingSessions')}
            </Typography>
          </StatsBox>
          <StatsBox>
            <StatsNumber variant="h3">1,200+</StatsNumber>
            <Typography variant="body1" color="text.secondary">
              {t('activeTeams')}
            </Typography>
          </StatsBox>
        </Box>
      </Container>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <SectionHeading variant="h3">{t('featuresTitle')}</SectionHeading>
        <SectionSubheading variant="body1">{t('heroSubtitle')}</SectionSubheading>
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={feature.title + index}>
              <FeatureCard>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <Typography variant="h6" gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <SectionHeading variant="h3">{t('landingHowItWorksTitle')}</SectionHeading>
        <SectionSubheading variant="body1">{t('heroSubtitle')}</SectionSubheading>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((step) => (
            <Grid item xs={12} md={6} key={step}>
              <StepCard>
                <StepNumber>{step}</StepNumber>
                <Box>
                  <Typography variant="h6">
                    {t(`step${step}Title`)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(`step${step}Desc`)}
                  </Typography>
                </Box>
              </StepCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <SectionHeading variant="h3">{t('benefitsTitle')}</SectionHeading>
        <SectionSubheading variant="body1">{t('heroSubtitle')}</SectionSubheading>
        <Grid container spacing={3}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} md={4} key={benefit.title + index}>
              <BenefitCard>
                <Box sx={{ mb: 2 }}>{benefit.icon}</Box>
                <Typography variant="h6" gutterBottom>
                  {benefit.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {benefit.description}
                </Typography>
              </BenefitCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container maxWidth="md" sx={{ py: 8 }}>
        <SectionHeading variant="h3">{t('faqTitle')}</SectionHeading>
        <SectionSubheading variant="body1">{t('heroSubtitle')}</SectionSubheading>
        {faqs.map((faq, index) => (
          <Accordion
            key={faq.question + index}
            elevation={0}
            sx={{
              mb: 1,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 2,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>

      <CTASection>
        <Container maxWidth="md">
          <CTAInner>
            <Box sx={{ textAlign: 'center', overflowWrap: 'break-word' }}>
              <Typography variant="h3" gutterBottom>
                {t('ctaTitle')}
              </Typography>
              <Typography variant="h6" paragraph sx={{ mb: 4 }} color="text.secondary">
                {t('ctaSubtitle')}
              </Typography>
              <CTAButton variant="contained" size="large" onClick={() => navigate('/login')}>
                {t('ctaButton')}
              </CTAButton>
            </Box>
          </CTAInner>
        </Container>
      </CTASection>
    </Page>
  );
}

export default LandingPage;
